import type { BadgeTone } from '@/components/ui/Badge';
import type { Option } from '@/constants/institute';
import type {
  FeeDueWhen,
  FeeHead,
  FeePaymentMethod,
  FeeStatus,
  ReminderPlaceholder,
} from '@/types/fees';

/** One page of the fee register. The same size the student roll uses. */
export const FEE_ACCOUNTS_PER_PAGE = 20;
export const FEE_RECEIPTS_PER_PAGE = 20;

/**
 * What the register offers as a page size.
 *
 * Capped well under the server's own `MAX_PAGE_SIZE` of 100: a counter clerk
 * scanning for one family wants ten rows, and an office reconciling a class
 * wants the class. Nothing above 100 is offered because nothing above 100 would
 * be served.
 */
export const FEE_PAGE_SIZES = [10, 20, 50, 100] as const;

/**
 * The order an office reads its register in: who is behind first, who is
 * settled last. Written out rather than derived from the type so the summary
 * keeps a deliberate order rather than an alphabetical one.
 */
export const FEE_STATUS_ORDER = ['UNPAID', 'PARTIAL', 'PAID', 'NOT_SET'] as const;

export const FEE_STATUS_LABEL = {
  PAID: 'Fully paid',
  PARTIAL: 'Part paid',
  UNPAID: 'Not paid',
  NOT_SET: 'No fees set',
} as const satisfies Record<FeeStatus, string>;

/**
 * Red is kept for the one that needs chasing.
 *
 * "Not paid" is the row an office acts on, so it carries the alarm. "Part paid"
 * is in progress rather than wrong, and "No fees set" is our own paperwork
 * missing rather than the family's — it reads as information, not as debt.
 */
export const FEE_STATUS_TONE = {
  PAID: 'success',
  PARTIAL: 'warning',
  UNPAID: 'critical',
  NOT_SET: 'neutral',
} as const satisfies Record<FeeStatus, BadgeTone>;

export const FEE_STATUS_OPTIONS: Option[] = [
  { value: '', label: 'Every account' },
  ...FEE_STATUS_ORDER.map((value) => ({ value, label: FEE_STATUS_LABEL[value] })),
];

export const FEE_SORT_OPTIONS: Option[] = [
  { value: 'balance', label: 'Largest balance first' },
  { value: 'payable', label: 'Largest payable first' },
  { value: 'collected', label: 'Most collected first' },
  { value: 'dueDate', label: 'Soonest due first' },
  { value: 'name', label: 'Name' },
  { value: 'admissionNumber', label: 'Admission number' },
];

/**
 * The heads a bill is kept in, as the register's "fee type" filter names them.
 *
 * Not a server filter: every account carries both heads, so choosing one does
 * not remove rows — it changes which figures the money columns report. An
 * office chasing books money wants the same families with the books numbers,
 * not a shorter list.
 */
export const FEE_HEAD_LABEL = {
  '': 'All fee types',
  tuition: 'Tuition fee',
  books: 'Books and materials',
  other: 'Other fees',
} as const satisfies Record<FeeHead, string>;

export const FEE_HEAD_OPTIONS: Option[] = (Object.keys(FEE_HEAD_LABEL) as FeeHead[]).map(
  (value) => ({ value, label: FEE_HEAD_LABEL[value] }),
);

/** How the counter names each way money arrives. */
export const FEE_METHOD_LABEL = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
  NETBANKING: 'Net banking',
  CHEQUE: 'Cheque',
  DEMAND_DRAFT: 'Demand draft',
  BANK_TRANSFER: 'Bank transfer',
} as const satisfies Record<FeePaymentMethod, string>;

/**
 * What goes in the reference box, per method.
 *
 * Everything but cash arrived through something with a number on it, and that
 * number is what reconciles the day's takings against the bank — so the field
 * is required for all of them, and the hint says exactly what to copy.
 */
export const FEE_METHOD_REFERENCE_HINT = {
  CASH: 'Optional for cash — a counter slip number, if your institute uses one.',
  UPI: 'The 12-digit UPI transaction id from the payment app.',
  CARD: 'The approval or RRN code on the card slip.',
  NETBANKING: 'The bank’s transaction reference.',
  CHEQUE: 'The cheque number, and the bank it is drawn on.',
  DEMAND_DRAFT: 'The DD number and the issuing bank.',
  BANK_TRANSFER: 'The NEFT, RTGS or IMPS reference.',
} as const satisfies Record<FeePaymentMethod, string>;

export const FEE_METHOD_OPTIONS: Option<FeePaymentMethod>[] = (
  Object.keys(FEE_METHOD_LABEL) as FeePaymentMethod[]
).map((value) => ({ value, label: FEE_METHOD_LABEL[value] }));

export const FEE_METHOD_FILTER_OPTIONS: Option[] = [
  { value: '', label: 'Every method' },
  ...FEE_METHOD_OPTIONS,
];

// --- chasing what is owed ---------------------------------------------------

/** Matches `REMINDER_MAX_LENGTH` on the server, which is the one that decides. */
export const REMINDER_MAX_LENGTH = 480;

/** One SMS is 160 characters; past that the institute is billed per part. */
export const SMS_PART_LENGTH = 160;

/**
 * One-click "owes at least" bands on the reminder dialog, in whole rupees — the
 * thresholds an office usually chases by. Anything else is typed in.
 */
export const REMINDER_DUE_PRESETS = [1000, 5000, 10000, 25000] as const;

/**
 * The due-date choices a reminder can be aimed by — on the office's dialog and
 * the class teacher's page alike. "Due in N days" counts today as day zero.
 */
export const DUE_WHEN_CHOICES = [
  { key: 'ANY', label: 'Any time' },
  { key: 'OVERDUE', label: 'Overdue', dueWhen: 'OVERDUE' },
  { key: 'DUE_7', label: 'Due in 7 days', dueWhen: 'DUE_SOON', dueWithinDays: 7 },
  { key: 'DUE_15', label: 'Due in 15 days', dueWhen: 'DUE_SOON', dueWithinDays: 15 },
  { key: 'DUE_30', label: 'Due in 30 days', dueWhen: 'DUE_SOON', dueWithinDays: 30 },
] as const satisfies readonly {
  key: string;
  label: string;
  dueWhen?: FeeDueWhen;
  dueWithinDays?: number;
}[];

export type DueWhenChoice = (typeof DUE_WHEN_CHOICES)[number]['key'];

/** A choice as the API takes it — nothing at all for "any time". */
export function dueWhenFilter(choice: DueWhenChoice): {
  dueWhen?: FeeDueWhen;
  dueWithinDays?: number;
} {
  const picked = DUE_WHEN_CHOICES.find((option) => option.key === choice);
  if (!picked || !('dueWhen' in picked)) return {};
  return 'dueWithinDays' in picked
    ? { dueWhen: picked.dueWhen, dueWithinDays: picked.dueWithinDays }
    : { dueWhen: picked.dueWhen };
}

/** A default message for a class teacher, who cannot see amounts — so none is quoted. */
export const DEFAULT_CLASS_REMINDER_MESSAGE =
  'Dear Parent, {installment} fees for {name} ({admissionNumber}) are due on {dueDate}. ' +
  'Please pay at the school office. — {institute}';

/**
 * What a reminder may say about the family reading it, and what each one fills
 * in — shown under the box, because a placeholder nobody knows about is a
 * feature nobody uses.
 */
export const REMINDER_PLACEHOLDER_HINT: Record<ReminderPlaceholder, string> = {
  name: "the student's full name",
  amount: 'what this family still owes',
  admissionNumber: 'their admission number',
  institute: "your institute's name",
  session: 'the academic session',
  installment: 'the next installment to pay, such as Term 2',
  dueDate: 'the day it falls due',
};

export const REMINDER_PLACEHOLDERS = Object.keys(
  REMINDER_PLACEHOLDER_HINT,
) as ReminderPlaceholder[];

/**
 * What the box says before anybody types.
 *
 * Written as a real reminder rather than as a demand: it names the child, the
 * amount and the session, so a parent can act on it without ringing to ask what
 * it is about — and it signs off with the institute, because an SMS from an
 * unknown number asking for money reads like a fraud otherwise.
 *
 * Deliberately no institute name, no amount and no date typed in: this product
 * serves many institutes (§4), and every one of those is a placeholder the
 * server fills in per family.
 */
export const DEFAULT_REMINDER_MESSAGE =
  'Dear Parent, fees of {amount} are still outstanding for {name} ({admissionNumber}) ' +
  'for the {session} session. Please pay at the school office. — {institute}';
